import React, { useEffect, useRef } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useCity } from './contexts/CityContext';
import { useAuth } from './contexts/AuthContext';
import { Sidebar } from './components/BottomNav';
import { SplashPage } from './pages/SplashPage';
import { CitySelectPage } from './pages/CitySelectPage';
import { MapPage } from './pages/MapPage';
import { FeedPage } from './pages/FeedPage';
import { BusinessDetailPage } from './pages/BusinessDetailPage';
import { DeliveriesPage } from './pages/DeliveriesPage';
import { ProfilePage } from './pages/ProfilePage';
import { LegalPage } from './pages/LegalPage';
import { AuthPage } from './pages/AuthPage';
import { AccountPage } from './pages/AccountPage';
import { MerchantRequestPage } from './pages/MerchantRequestPage';
import { MerchantBusinessesPage } from './pages/MerchantBusinessesPage';
import { MerchantBusinessEditPage } from './pages/MerchantBusinessEditPage';
import { MerchantPostsPage } from './pages/MerchantPostsPage';
import { BoostPage } from './pages/BoostPage';
import { BuyCreditsPage } from './pages/BuyCreditsPage';
import { SalesReportPage } from './pages/SalesReportPage';
import { CityEventsPage } from './pages/CityEventsPage';
import { EventDetailPage } from './pages/EventDetailPage';
import { CreateEventPage } from './pages/CreateEventPage';
import { DeliveryProfilePage } from './pages/DeliveryProfilePage';
import { AdminDashboardPage } from './pages/AdminDashboardPage';
import { AdminBusinessesPage } from './pages/AdminBusinessesPage';
import { AdminCitiesPage } from './pages/AdminCitiesPage';
import { AdminModerationPage } from './pages/AdminModerationPage';
import { AdminInvitesPage } from './pages/AdminInvitesPage';
import { AdminImportsPage } from './pages/AdminImportsPage';
import { AdminClaimsPage } from './pages/AdminClaimsPage';
import { FavoritesPage } from './pages/FavoritesPage';
import { OrdersPage } from './pages/OrdersPage';
import { OrderDetailPage } from './pages/OrderDetailPage';
import { NotificationsPage } from './pages/NotificationsPage';
import { HelpPage } from './pages/HelpPage';
import { DeliveryOrdersPage } from './pages/DeliveryOrdersPage';

// LE-RESP: rotas "standalone" (splash, escolha de cidade, login, páginas LGPD)
// não exibem a sidebar — todo o restante usa o shell com navegação lateral.
// LE-039: /mapa também é standalone — o mapa ocupa a TELA TODA (fullscreen),
// com a navegação embutida no header flutuante (feed/entregas/perfil) em vez
// da sidebar, priorizando a experiência de mapa.
const STANDALONE_PATHS = ['/', '/cidade', '/entrar', '/privacidade', '/termos', '/lgpd', '/contato', '/mapa'];

// LE-010Y: guarda simples por papel — o backend continua sendo a autoridade real
function RequireAuth({ children, roles }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/entrar" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/perfil" replace />;
  return children;
}

function App() {
  const { selectedCity } = useCity();
  const location = useLocation();
  const mainRef = useRef(null);

  // LE-031: o main é o container de rolagem — volta ao topo a cada navegação
  // (comportamento web padrão; sem isso a posição do scroll persistiria)
  useEffect(() => {
    if (mainRef.current) mainRef.current.scrollTop = 0;
  }, [location.pathname]);

  const showShell = !STANDALONE_PATHS.includes(location.pathname);

  return (
    <>
      {showShell && <Sidebar />}
      <main
        id="main-content"
        ref={mainRef}
        className={showShell ? 'nc-app-main nc-app-main--shell' : 'nc-app-main'}
        style={{
          height: '100vh',
          overflowY: 'auto',
          overflowX: 'hidden',
          WebkitOverflowScrolling: 'touch',
        }}
      >
      {/* LE-031: o main é o container de rolagem — as telas com minHeight:100vh
          (a grande maioria) crescem com o conteúdo e rolam verticalmente quando
          ultrapassam o viewport (antes, overflow:hidden cortava o conteúdo). O
          MapPage mantém height:100vh própria (mapa fullscreen, sem rolagem). */}
      <Routes>
        {/* LE-008: Páginas LGPD (não exigem cidade selecionada) */}
        <Route path="/privacidade" element={<LegalPage />} />
        <Route path="/termos" element={<LegalPage />} />
        <Route path="/lgpd" element={<LegalPage />} />
        <Route path="/contato" element={<LegalPage />} />

        <Route path="/" element={<SplashPage />} />
        <Route path="/cidade" element={<CitySelectPage />} />

        {/* Autenticação e conta (não exigem cidade) */}
        <Route path="/entrar" element={<AuthPage />} />
        <Route path="/minha-conta" element={
          <RequireAuth><AccountPage /></RequireAuth>
        } />

        <Route
          path="/mapa"
          element={selectedCity ? <MapPage /> : <Navigate to="/" />}
        />
        <Route
          path="/feed"
          element={selectedCity ? <FeedPage /> : <Navigate to="/" />}
        />
        <Route
          path="/comercio/:id"
          element={selectedCity ? <BusinessDetailPage /> : <Navigate to="/" />}
        />
        <Route
          path="/entregas"
          element={selectedCity ? <DeliveriesPage /> : <Navigate to="/" />}
        />
        <Route
          path="/perfil"
          element={selectedCity ? <ProfilePage /> : <Navigate to="/" />}
        />

        {/* LE-010Y: área do comerciante — qualquer usuário autenticado pode solicitar;
            o backend valida propriedade (owner_id) em cada ação */}
        <Route path="/comerciante/solicitar" element={
          <RequireAuth><MerchantRequestPage /></RequireAuth>
        } />
        <Route path="/comerciante/estabelecimentos" element={
          <RequireAuth><MerchantBusinessesPage /></RequireAuth>
        } />
        <Route path="/comerciante/comercio/:id" element={
          <RequireAuth><MerchantBusinessEditPage /></RequireAuth>
        } />
        <Route path="/comerciante/publicacoes" element={
          <RequireAuth><MerchantPostsPage /></RequireAuth>
        } />
        {/* LE-020 (S4-b): impulsionamento — planos, compra de boost e extrato de créditos */}
        <Route path="/comerciante/impulsionar/:businessId?" element={
          <RequireAuth><BoostPage /></RequireAuth>
        } />
        {/* Comprar créditos via Pix */}
        <Route path="/comprar-creditos" element={
          <RequireAuth><BuyCreditsPage /></RequireAuth>
        } />
        {/* Relatório de vendas (admin) */}
        <Route path="/admin/relatorio" element={
          <RequireAuth roles={['admin']}><SalesReportPage /></RequireAuth>
        } />

        {/* LE-011: favoritos e pedidos do usuário */}
        <Route path="/favoritos" element={
          <RequireAuth><FavoritesPage /></RequireAuth>
        } />
        <Route path="/pedidos" element={
          <RequireAuth><OrdersPage /></RequireAuth>
        } />
        {/* LE-018: detalhe do pedido com linha do tempo */}
        <Route path="/pedido/:id" element={
          <RequireAuth><OrderDetailPage /></RequireAuth>
        } />

        {/* LE-014: notificações (autenticado) e ajuda (pública) */}
        <Route path="/notificacoes" element={
          <RequireAuth><NotificationsPage /></RequireAuth>
        } />
        <Route path="/ajuda" element={<HelpPage />} />

        {/* LE-020 (S4-c): eventos da cidade — listagem e detalhe públicos */}
        <Route path="/eventos" element={
          selectedCity ? <CityEventsPage /> : <Navigate to="/" />
        } />
        <Route path="/evento/:id" element={
          selectedCity ? <EventDetailPage /> : <Navigate to="/" />
        } />
        <Route path="/eventos/novo" element={
          <RequireAuth><CreateEventPage /></RequireAuth>
        } />
        <Route path="/eventos/:id/editar" element={
          <RequireAuth><CreateEventPage /></RequireAuth>
        } />

        {/* LE-010Y/LE-015: área do entregador */}
        <Route path="/entregador/perfil" element={
          <RequireAuth><DeliveryProfilePage /></RequireAuth>
        } />
        <Route path="/entregador/pedidos" element={
          <RequireAuth><DeliveryOrdersPage /></RequireAuth>
        } />

        {/* LE-010Y: painel OthoSystems (admin) */}
        <Route path="/admin" element={
          <RequireAuth roles={['admin']}><AdminDashboardPage /></RequireAuth>
        } />
        <Route path="/admin/comercios" element={
          <RequireAuth roles={['admin']}><AdminBusinessesPage /></RequireAuth>
        } />
        <Route path="/admin/cidades" element={
          <RequireAuth roles={['admin']}><AdminCitiesPage /></RequireAuth>
        } />
        <Route path="/admin/moderacao" element={
          <RequireAuth roles={['admin']}><AdminModerationPage /></RequireAuth>
        } />
        <Route path="/admin/convites" element={
          <RequireAuth roles={['admin']}><AdminInvitesPage /></RequireAuth>
        } />
        <Route path="/admin/importacoes" element={
          <RequireAuth roles={['admin']}><AdminImportsPage /></RequireAuth>
        } />
        <Route path="/admin/reivindicacoes" element={
          <RequireAuth roles={['admin']}><AdminClaimsPage /></RequireAuth>
        } />

        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
      </main>
    </>
  );
}

export default App;
